import { globalHistory } from '@reach/router';
import { Link, navigate } from 'gatsby';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import BodyText from '@ui/atoms/BodyText';
import Button from '@ui/atoms/Button';
import Eyebrow from '@ui/atoms/Eyebrow';
import Header from '@ui/atoms/Header';
import TextButton from '@ui/atoms/TextButton';

import TextInput from '@ui/forms/TextInput';

import { useTracking } from '@hooks/useTracking';

import { ResetPasswordError, useAuth } from '@providers/AuthProvider';

import theme from '@theme/FolxTheme';

interface IResetPasswordWithCodeFormFields {
	email: string;
	password: string;
	code: string;
	confirmPassword?: string;
}

interface IResetPasswordWithCodeProps {
	location: {
		state: {
			email: string;
		};
	};
}

const ErrorMessage = styled.div`
	color: ${theme.colors.red};
`;

const PageWrapper = styled.div`
	background: ${theme.colors.black};
	height: 100vh;
	display: flex;
	flex-flow: column;
`;

const ResetFormContainer = styled.div`
	background: ${theme.colors.lightGray};
	padding: 2rem 2.4rem;
	display: flex;
	flex-flow: column;
	align-content: center;
	width: 100%;
	max-width: 64rem;
	margin: 6.4rem auto 0 auto;

	${Header} {
		margin-bottom: 2.4rem;
	}

	${Button} {
		width: 100%;
	}
`;

const FormWrapper = styled.div`
	display: flex;
	flex-flow: column;
	margin-top: 3.2rem;
	margin-bottom: 3.2rem;

	> label + input {
		margin-top: 1.2rem;
	}

	> input + label {
		margin-top: 3.6rem;
	}
`;

const ResetButton = styled(TextButton)`
	${Eyebrow} {
		text-transform: none;
		padding-left: 0.8rem;
	}
`;

const ResetCodeContainer = styled.div`
	display: flex;
	margin-bottom: 3.2rem;
`;

const ResetPasswordWithCode = ({
	location: {
		state: { email },
	},
}: IResetPasswordWithCodeProps) => {
	const {
		register,
		handleSubmit,
		setError,
		getValues,

		formState: { errors },
	} = useForm<IResetPasswordWithCodeFormFields>({
		defaultValues: {
			email,
		},
	});

	const { resetPassword, requestPasswordReset } = useAuth();

	const [expiredCode, setExpiredCode] = useState(false);
	const [resendStatus, setResetStatus] = useState(false);
	const { trackEvent } = useTracking('reset-password-form-viewed');

	const onResetFormSubmit = async ({ email, code, password }: IResetPasswordWithCodeFormFields) => {
		try {
			setExpiredCode(false);
			setResetStatus(false);
			await resetPassword(email, code, password);
			trackEvent('reset-password-success');
			await navigate(globalHistory.location.state?.redirectTo ?? '/app/sign_in', {
				state: { passwordReset: true },
			});
		} catch (error) {
			console.log(`Caught error while submitting password reset code: ${error}`);
			if (error instanceof ResetPasswordError) {
				switch (error.type) {
					case 'WRONG_CODE':
						setError('code', {
							message: 'Code does not match. Please try again.',
						});
						break;
					case 'EXPIRED_CODE':
						setError('code', {
							message: 'Code has expired. Please request a new one.',
						});
						setExpiredCode(true);
						break;
					default:
						setError('email', { message: error.message });
				}
			} else {
				setError('email', { message: error?.message ?? 'Unknown error' });
			}
		}
	};

	const getPasswordErrorMessage = () => {
		if (errors.password?.message) {
			return errors.password?.message;
		}
		switch (errors.password?.type) {
			case 'minLength':
				return 'Your password needs to be at least ten characters long.';
			case 'atLeastOneUpperCaseLetter':
				return 'Your password needs at least upper case letter';
			case 'atLeastOneLowerCaseLetter':
				return 'Your password needs at least lower case letter';
			case 'atLeastOneNumber':
				return 'Your password needs at least one number';
			case 'required':
			default:
				return 'A valid password is required.';
		}
	};

	function renderRequestCodeForm() {
		return (
			<ResetFormContainer>
				<header>
					<h1>Let's reset your password.</h1>
				</header>
				<form onSubmit={handleSubmit(onResetFormSubmit)}>
					<FormWrapper>
						<label htmlFor="email">
							<BodyText>Email</BodyText>
						</label>
						<TextInput readOnly={!!email} {...register('email')} />
						<label htmlFor="code">
							<BodyText>Code</BodyText>
						</label>
						<TextInput
							{...register('code', {
								required: true,
							})}
						/>
						<label htmlFor="password">
							<BodyText>Password</BodyText>
						</label>
						<TextInput
							type="password"
							{...register('password', {
								required: true,
								validate: {
									atLeastOneLowerCaseLetter: (value) => /.*[a-z].*/.test(value),
									atLeastOneUpperCaseLetter: (value) => /.*[A-Z].*/.test(value),
									atLeastOneNumber: (value) => /.*[0-9].*/.test(value),
								},
								minLength: 10,
								maxLength: 20,
							})}
						/>
						<label htmlFor="confirmPassword">
							<BodyText>Please confirm your new password</BodyText>
						</label>
						<TextInput
							type="password"
							{...register('confirmPassword', {
								required: true,
								validate: (field) => getValues('password') === field,
							})}
						/>
					</FormWrapper>
					{errors.password && <ErrorMessage>{getPasswordErrorMessage()}</ErrorMessage>}
					{errors.confirmPassword && <ErrorMessage>Passwords don't match!</ErrorMessage>}
					{errors.code && (
						<ErrorMessage>
							{errors.code.message ? errors.code.message : `Code is required.`}
						</ErrorMessage>
					)}
					<ResetCodeContainer>
						{resendStatus ? (
							<BodyText>A new verification code has been sent to your email address.</BodyText>
						) : (
							<>
								<BodyText>Need a new verification code? {'  '}</BodyText>
								<ResetButton
									onClick={() =>
										requestPasswordReset(email)
											.then(() => setResetStatus(true))
											.catch(() =>
												setError('code', {
													message: 'An error occurred while resending code. Please try again.',
												})
											)
									}
									label="Click here to request a new one."
								/>
							</>
						)}
					</ResetCodeContainer>
					{errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
					<Button type="submit" label="Reset password" />
				</form>
			</ResetFormContainer>
		);
	}

	function renderSuccessMessage() {
		return (
			<div className="success">
				<p>Your password has been reset.</p>
				<p>
					<Link to="/app/sign_in">Click here to login with your new credentials.</Link>
				</p>
			</div>
		);
	}

	return <PageWrapper>{renderRequestCodeForm()}</PageWrapper>;
};

export default ResetPasswordWithCode;
